/**
 * TanStack Query key factory — hierarchical keys for stable invalidation.
 *
 * Convention (research-B §5.2):
 *   qk.<resource>.all           → invalidate all under this resource
 *   qk.<resource>.list(filter)  → list scoped by filter
 *   qk.<resource>.detail(id)    → single record
 *
 * Phase 1 keys cover only the surface that ships in this milestone (auth +
 * users/me, departments, roles); other resources are stubbed so calls from
 * future phases land on a known prefix without rename churn.
 *
 * `as const` everywhere keeps the keys structurally typed — passing the
 * factory output to React Query gives you key inference without manual
 * generics.
 */

export const qk = {
  auth: {
    all: ['auth'] as const,
    session: () => ['auth', 'session'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (filter?: Readonly<Record<string, unknown>>) => ['users', 'list', filter ?? {}] as const,
    detail: (id: string) => ['users', 'detail', id] as const,
    me: () => ['users', 'me'] as const,
  },
  departments: {
    all: ['departments'] as const,
    list: () => ['departments', 'list'] as const,
  },
  roles: {
    all: ['roles'] as const,
    list: () => ['roles', 'list'] as const,
  },
  // Блок 2 · Ф8 · В-Э — экран прав. Ключи названы планом фазы: карточка
  // человека — `['user-rights', id]`, сводный список — `['user-rights', 'list']`.
  // Идентификатор человека — UUID, со словом `list` он не совпадёт; общий
  // префикс `all` сбрасывает и список, и карточки ВСЕХ людей разом — запрет,
  // поставленный ГИПу, меняет карточки его помощников (Р-197).
  userRights: {
    all: ['user-rights'] as const,
    card: (userId: string) => ['user-rights', userId] as const,
    list: () => ['user-rights', 'list'] as const,
  },
  // Phase-2 Wave-5: Supervisor settings (encrypted-at-rest key/value store).
  settings: {
    all: ['settings'] as const,
    list: () => ['settings', 'list'] as const,
  },
  // M2 Phase 7 W5: per-user notification prefs + VK Teams binding. Both
  // surfaces hydrate from `useMe()` (auth.session()) on the happy path,
  // but their dedicated cache slots exist so the bind-modal poll and the
  // optional standalone notifications read can bust independently of the
  // /auth/me cache (which carries a lot more state we don't want to
  // re-fetch on every 3-second poll).
  notifications: {
    all: ['notifications'] as const,
    me: () => ['notifications', 'me'] as const,
  },
  vkBinding: {
    all: ['vk-binding'] as const,
    me: () => ['vk-binding', 'me'] as const,
    /**
     * Какие мессенджеры бота настроены — `GET /users/me/bot-channels`.
     *
     * 🚫 ⚠️ УТВЕРЖДЕНИЕ СНЯТО КАК ЛОЖНОЕ — фикс-пак круга ревью 03.09.2026.
     * Стояло: ~~«ЛЕЖИТ ПОД `vk-binding`, НО ПОД `all` НЕ ГАСИТСЯ ОСМЫСЛЕННО:
     * ответ зависит от настроек супервайзера, а не от привязки человека,
     * поэтому отвязка его не устаревает. Соседство — ради читаемости кэша, а
     * не ради общей инвалидации»~~.
     *
     * ⚠️ ЧТО НА САМОМ ДЕЛЕ: react-query 5 сопоставляет ключи ПРЕФИКСНО
     * (`partialMatchKey` в `@tanstack/query-core`: для массива-образца
     * сверяются только его собственные позиции), поэтому
     * `invalidateQueries({ queryKey: qk.vkBinding.all })` попадает и сюда.
     * Гашение ПРОИСХОДИТ — и на отвязке (`useUnbindVkMutation`), и на успешной
     * привязке (опрос в `vk-bind-dialog`).
     *
     * ⚠️ ПОСЛЕДСТВИЙ У ЭТОГО НЕТ, И ИМЕННО ПОЭТОМУ ОНО ПРОЖИЛО НЕЗАМЕЧЕННЫМ:
     * лишний перезапрос настроек стоит одного дешёвого чтения, а `refetchType:
     * 'active'` не трогает то, чего никто не смотрит. Снято не ради поведения,
     * а потому что следующий будет строить на этой фразе решение — например,
     * «раз под `all` не гасится, заведу гашение руками» — и построит на
     * неправде.
     *
     * ⚠️ СОСЕДСТВО ПОД `vk-binding` ОСТАЁТСЯ ОСОЗНАННЫМ ВЫБОРОМ: ключ и правда
     * про настройки супервайзера, а не про привязку человека. Неверна была
     * ровно механика («не гасится»), а не размещение.
     */
    channels: () => ['vk-binding', 'channels'] as const,
  },
  // ─── Phase-4 surface (W4.T3) ───────────────────────────────────────────
  // Hierarchy mirrors `qk.users.*` — `all` for coarse bust, `list(filter)`
  // for filtered reads, `detail(id)` for the single-row cache slot.
  // Sub-resources (`team`, `unplanned`, `audit`) live UNDER the project id
  // so a single project-scoped invalidation can target one without
  // touching the others; `all` still busts every project key in lock-step.
  //
  // Invalidation rules (research-frontend §6.1, PLAN.md T4.1):
  //   - create / archive / restore project → invalidate `qk.projects.all`
  //     AND `qk.workload.all` (workload grid embeds the project list).
  //   - team mutations (add/remove manager, add/remove participant,
  //     promote-from-fact) → invalidate `qk.projects.detail(id)`
  //     (carries managers[]/participants[] post-FT.A3),
  //     `qk.projects.unplanned(id)`, `qk.projects.audit(id, …)`
  //     AND `qk.workload.all`.
  //   - any project mutation → invalidate `qk.projects.audit(id, …)` so
  //     the «Журнал» tab reflects the new audit row without a manual
  //     refresh.
  //
  // FT.B2: `qk.projects.team` and `qk.projects.reporters` were removed
  // alongside the orphan endpoints `/team` and `/reporters` that never
  // existed on the BE.
  projects: {
    all: ['projects'] as const,
    list: (filter?: Readonly<Record<string, unknown>>) =>
      ['projects', 'list', filter ?? {}] as const,
    detail: (id: string) => ['projects', 'detail', id] as const,
    unplanned: (id: string) => ['projects', 'detail', id, 'unplanned'] as const,
    /**
     * R6 Wave 3 — `GET /projects/:id/team-fact-hours`. Cohort is
     * `ProjectParticipant` join with `WorkloadFact` SUM. Bumped by the
     * remove-participant mutation (DA-M9) so the «Сотрудники» panel
     * refreshes when a row is removed.
     */
    teamFactHours: (id: string) => ['projects', 'detail', id, 'team-fact-hours'] as const,
    /**
     * Project audit log — `GET /projects/:id/audit?limit&offset`. Filter
     * carries the offset/limit pair so each page caches independently
     * (round-4 T4.8: offset-based, NOT cursor-based — do NOT mirror the
     * `WorkloadHistoryPanel` `nextCursor` shape here).
     */
    audit: (id: string, filter?: Readonly<Record<string, unknown>>) =>
      ['projects', 'detail', id, 'audit', filter ?? {}] as const,
    /**
     * M2 «Финблок» Фича 2 — `GET /projects/:id/expenses` (list + total).
     * Nested under the project detail key so a project-detail invalidation
     * (or an explicit expenses bust) refreshes the tab and the «Обзор» total.
     */
    expenses: (id: string) => ['projects', 'detail', id, 'expenses'] as const,
    /**
     * Block 2 «Трекер объектов», Этап 01a — `GET /projects/:id/sections`
     * (list + per-section assignment roster). Nested under the project detail
     * key so a section / assignment mutation (which also lands a «Журнал» row)
     * can bust the sections tab and the audit slice in lock-step.
     */
    sections: (id: string) => ['projects', 'detail', id, 'sections'] as const,
    /**
     * Block 2 «Трекер объектов», Ф3 · В3a — `GET /projects/:id/team`
     * («команда объекта»: разделы с работами + участники без работ).
     *
     * ⚠️ КЛЮЧ С ТАКИМ ИМЕНЕМ ЗДЕСЬ УЖЕ БЫЛ И БЫЛ СНЯТ — см. пометку «FT.B2»
     * в шапке этого блока: тогда `/team` был осиротевшим маршрутом, которого
     * на сервере не существовало. **Теперь он существует** (`ProjectTeamController`,
     * Ф2 · В2b), поэтому ключ заводится заново и СОЗНАТЕЛЬНО. Пометка FT.B2
     * оставлена на месте намеренно: она объясняет, почему ключа не было, и без
     * неё следующий читатель снимет этот как «уже удалявшийся».
     *
     * Лежит ПОД `projects.detail(id)`, чтобы мутация работы, бьющая по детали
     * объекта, обновляла состав команды тем же действием.
     */
    team: (id: string) => ['projects', 'detail', id, 'team'] as const,
    /**
     * Блок 2 «Трекер объектов», Ф7 · В11-c — `GET /projects/:id/source-links`
     * (ссылки на исходные данные объекта).
     *
     * Лежит ПОД `projects.detail(id)` по той же причине, что разделы и
     * расходы: каждая мутация ссылки кладёт строку в «Журнал» объекта, и
     * сброс детали обязан утаскивать за собой обе вкладки разом.
     */
    sourceLinks: (id: string) => ['projects', 'detail', id, 'source-links'] as const,
    /**
     * Блок 2 · Ф7 · В11-b — `GET /projects/complex-names`: существующие
     * написания комплексов. Кормит и комбобокс формы объекта, и отбор списка.
     *
     * ⚠️ ЛЕЖИТ ПОД `projects`, И ЭТО НЕСУЩЕЕ. Список написаний —
     * ПРОИЗВОДНАЯ от объектов: правка `complexName` у одного объекта меняет
     * его целиком. Лежи ключ отдельным поддеревом, гашение `qk.projects.all`
     * его бы не задело, и следующая форма предложила бы старый набор
     * подсказок — молча и правдоподобно.
     *
     * Соседства с `detail(id)` нет намеренно: значение глобальное, а не
     * принадлежит объекту.
     */
    complexNames: () => ['projects', 'complex-names'] as const,
  },
  /**
   * Блок 2 · Ф2 · В2c (API) → Ф7 · В11-b (UI) — справочник договоров.
   *
   * Верхний уровень, а не ветка `projects`: договор существует без объектов и
   * привязывается к ним, а не наоборот. Положи его под `projects`, и гашение
   * после правки объекта перезапрашивало бы весь справочник.
   *
   * ⚠️ `list(filter)` несёт фильтр архивности целиком: у ответа `active` и
   * `archived` разный состав строк, и общий слот отдал бы выпадающему списку
   * выбора договора архивные записи, которые выбирать нельзя.
   */
  contracts: {
    all: ['contracts'] as const,
    list: (filter?: Readonly<Record<string, unknown>>) =>
      ['contracts', 'list', filter ?? {}] as const,
    detail: (id: string) => ['contracts', 'detail', id] as const,
  },
  /**
   * Block 2 «Трекер объектов», Этап 2 — the §5.2 «справочник разделов
   * проектирования» (`GET /section-catalog`). GLOBAL reference-data, NOT
   * project-scoped, so it lives at the top level (a project-detail invalidation
   * must not bust it). `tree(includeArchived)` keeps the picker's ACTIVE-only
   * read and the supervisor editor's `includeArchived` read in disjoint cache
   * slots; supervisor CRUD mutations bust `all`.
   */
  sectionCatalog: {
    all: ['section-catalog'] as const,
    tree: (includeArchived = false) => ['section-catalog', 'tree', includeArchived] as const,
  },
  clients: {
    all: ['clients'] as const,
    list: (filter?: Readonly<Record<string, unknown>>) =>
      ['clients', 'list', filter ?? {}] as const,
    detail: (id: string) => ['clients', 'detail', id] as const,
    // NOTE (FT.B2): `qk.clients.projects` was removed alongside the orphan
    // endpoint `/clients/:id/projects`. Projects-of-client surface should
    // use `useProjectsQuery({ clientId })` (ADMIN-only filter today).
  },
  // ─── Phase-3 surface (W3.T1) ───────────────────────────────────────────
  // Three sub-resources share the `workload` prefix so a coarse
  // `qc.invalidateQueries({ queryKey: qk.workload.all })` busts every cached
  // grid / heatmap / changelog at once when no surgical target is known
  // (e.g. on bulk archive).
  workload: {
    all: ['workload'] as const,
    grid: (filter: Readonly<Record<string, unknown>>) => ['workload', 'grid', filter] as const,
    /**
     * Ф3 · В2d — БЕЗФИЛЬТРОВАЯ ручка семейства сетки, по той же нужде, что
     * `heatmapAll` / `ganttAll`.
     *
     * Заведена, когда мутация работы стала менять то, что показывает сетка:
     * подстроки разделов `rows[].sections` и список выбора раздела в окне
     * отчёта о часах читаются из НАЗНАЧЕНИЯ, а назначение с В2d — это работа.
     * До В2d сетка читала снесённую таблицу `SectionAssignment`, мутация работы
     * её не касалась, и сбрасывать было нечего.
     */
    gridAll: () => ['workload', 'grid'] as const,
    heatmap: (filter: Readonly<Record<string, unknown>>) =>
      ['workload', 'heatmap', filter] as const,
    /**
     * Ф3 · В3a — БЕЗФИЛЬТРОВАЯ ручка семейства тепловой карты.
     *
     * Заведена затем, чтобы мутация работы могла сбросить ВСЕ закэшированные
     * периоды карты одним ключом, не сбрасывая при этом сетку и журнал ячейки
     * (`qk.workload.all` снёс бы и их). Без такой ручки потребитель собирал бы
     * массив `['workload','heatmap']` руками у себя в файле — и первая же
     * правка формы ключа разошлась бы с ним молча.
     */
    heatmapAll: () => ['workload', 'heatmap'] as const,
    /**
     * Ф3 · В3a — `GET /workload/gantt` (график занятости, Ф2 · В2a).
     *
     * Своё поддерево, а не ветка `grid`: у Ганта свой маршрут, свой атом
     * (`workload.gantt.read`) и свой набор фильтров. Слив его с сеткой,
     * В4 не смогла бы сбросить один кэш, не задев другой.
     */
    gantt: (filter: Readonly<Record<string, unknown>>) => ['workload', 'gantt', filter] as const,
    /** Безфильтровая ручка семейства Ганта — см. `heatmapAll`. */
    ganttAll: () => ['workload', 'gantt'] as const,
    changelog: (filter: Readonly<Record<string, unknown>>) =>
      ['workload', 'changelog', filter] as const,
  },
  /**
   * ─── Блок 2 «Трекер объектов», Ф3 · В3a — «работы» ────────────────────
   *
   * Маршруты модуля `work/` (Ф2 · В2a): `GET /works`, `/works/mine`,
   * `/works/overlap`, `/works/:id`.
   *
   * ПЕРЕКРЁСТНАЯ ИНВАЛИДАЦИЯ — не деталь реализации, а причина, по которой
   * эти ключи заведены ОДНОЙ волной раньше трёх своих потребителей. Одна
   * работа видна на четырёх поверхностях сразу: в карточке объекта (дерево
   * разделов и команда), на Ганте, на тепловой карте и на личном экране «Моя
   * работа». Мутация, обновившая только своё поддерево, оставила бы три
   * экрана с устаревшими данными — и заметили бы это не сразу, потому что
   * каждый из них выглядит исправным.
   *
   * Состав сброса собран в ОДНОМ месте — `worksCrossBustKeys()` в
   * `@/api/works`; сюда он не выносится, потому что зависит от `projectId`
   * конкретной работы (у задачи его нет вовсе).
   */
  works: {
    all: ['works'] as const,
    list: (filter?: Readonly<Record<string, unknown>>) => ['works', 'list', filter ?? {}] as const,
    /** `GET /works/mine` — личный экран сотрудника (В5, Ф4). */
    mine: (filter?: Readonly<Record<string, unknown>>) => ['works', 'mine', filter ?? {}] as const,
    /** Безфильтровая ручка «моих работ» — см. `workload.heatmapAll`. */
    mineAll: () => ['works', 'mine'] as const,
    /**
     * `GET /works/assigned` — вид «я поручил» доски задач (В6, Ф4 · Р-19).
     *
     * ⚠️ СВОЙ КЛЮЧ, А НЕ ФИЛЬТР К `mine`. Решение заказчика называет «я
     * поручил» ВТОРЫМ ИСТОЧНИКОМ ДАННЫХ, и предикат у него другой (автор
     * строки, а не исполнитель). Общий ключ отдал бы переключателю доски чужую
     * выдачу при первом же переключении — и выглядело бы это как «доска
     * показывает не то», а не как ошибка кэша.
     */
    assigned: (filter?: Readonly<Record<string, unknown>>) =>
      ['works', 'assigned', filter ?? {}] as const,
    /** Безфильтровая ручка «я поручил» — парная к `mineAll`. */
    assignedAll: () => ['works', 'assigned'] as const,
    detail: (id: string) => ['works', 'detail', id] as const,
    /**
     * `GET /works/overlap` — счётчик параллельных работ в форме назначения.
     * Ключ несёт фильтр целиком (`userId` + период + исключаемая работа):
     * два разных периода — два разных ответа, и общий слот дал бы форме
     * чужое число.
     */
    overlap: (filter: Readonly<Record<string, unknown>>) => ['works', 'overlap', filter] as const,
  },
  absences: {
    all: ['absences'] as const,
    list: (filter: Readonly<Record<string, unknown>>) => ['absences', 'list', filter] as const,
    mine: (filter: Readonly<Record<string, unknown>>) => ['absences', 'mine', filter] as const,
    active: (filter: Readonly<Record<string, unknown>>) => ['absences', 'active', filter] as const,
  },
  profile: {
    all: ['profile'] as const,
    me: () => ['profile', 'me'] as const,
    detail: (userId: string) => ['profile', 'detail', userId] as const,
  },
  audit: { all: ['audit'] as const },
  // ─── Phase-5 surface (W3.T3.3) ─────────────────────────────────────────
  // Dashboard KPI summary + sub-resources. The `kpi(scope, deptDiscriminator)`
  // factory keeps ADMIN ('org') and MANAGER (numeric deptId) caches
  // disjoint without a nullable trick (DA WR-6: 'org' literal vs deptId).
  // `qk.dashboard.all` is the cross-bust handle used by Phase-1..4 mutations
  // (T3.5) — invalidating it cancels stale dashboard data after a project
  // edit, workload change, etc.
  dashboard: {
    all: ['dashboard'] as const,
    kpi: (scope: 'manager' | 'admin', deptDiscriminator: number | 'org') =>
      ['dashboard', scope, 'kpi', deptDiscriminator] as const,
    project: (projectId: string) => ['dashboard', 'project', projectId] as const,
  },
  /**
   * Блок 2 · Ф7 · В13′ — сводный ряд дашборда (`GET /summary`).
   *
   * ⚠️ КЛЮЧ СВОЙ, А НЕ ВЕТКА `dashboard`, И ЭТО НЕСУЩЕЕ. Когорта у ряда другая
   * («по бюро», не по отделу), поэтому дискриминатора отдела у него нет вовсе —
   * ответ одинаков для всех, кому роут отвечает `200`. Положи ключ под
   * `dashboard`, и общий сброс `qk.dashboard.all` начал бы гасить сводку вместе
   * с плитками — что само по себе не беда, но обратное опаснее: ключ с чужим
   * дискриминатором отдела развёл бы ОДИН ответ по нескольким кэшам.
   */
  summary: {
    all: ['summary'] as const,
    row: () => ['summary', 'row'] as const,
  },
  /**
   * Блок 2 · Ф7 · В11-a — лента уведомлений (`GET /notifications/me`).
   *
   * ⚠️ КЛЮЧ СВОЙ, А НЕ ВЕТКА `notifications`, И ЭТО НЕСУЩЕЕ. Под
   * `notifications` лежат НАСТРОЙКИ напоминаний, и сохранение настроек гасит
   * весь префикс `qk.notifications.all` — лента перезапрашивалась бы на
   * каждое сохранение формы, к которой отношения не имеет.
   *
   * ⚠️ ИДЕНТИФИКАТОР ПОЛЬЗОВАТЕЛЯ В КЛЮЧЕ — тоже несущее: ответ у каждого
   * свой, а вкладка переживает смену учётной записи. Без него второй вошедший
   * увидел бы ленту первого до ближайшего перезапроса.
   */
  notificationFeed: {
    all: ['notification-feed'] as const,
    mine: (userId: string) => ['notification-feed', userId] as const,
  },
} as const;

export type QueryKey = ReturnType<
  | typeof qk.auth.session
  | typeof qk.users.list
  | typeof qk.users.detail
  | typeof qk.users.me
  | typeof qk.departments.list
  | typeof qk.roles.list
>;
